import type {
  CreateTrainingSkillItemInput,
  TrainingSkillItemImportResult,
  TrainingSkillItemResponse,
  UpdateTrainingSkillItemInput,
} from '@lms/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

const skillItemsKey = (trainingId: string) => ['training-skill-items', trainingId]
const skillItemsPath = (trainingId: string) => `/api/trainings/${trainingId}/skill-items`

export function useTrainingSkillItems(trainingId: string) {
  return useQuery({
    queryKey: skillItemsKey(trainingId),
    queryFn: () => api.get<TrainingSkillItemResponse[]>(skillItemsPath(trainingId)),
    enabled: !!trainingId,
  })
}

export function useCreateTrainingSkillItem(trainingId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateTrainingSkillItemInput) => api.post(skillItemsPath(trainingId), data),
    onSuccess: () => qc.invalidateQueries({ queryKey: skillItemsKey(trainingId) }),
  })
}

export function useUpdateTrainingSkillItem(trainingId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: UpdateTrainingSkillItemInput }) =>
      api.put(`${skillItemsPath(trainingId)}/${itemId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: skillItemsKey(trainingId) }),
  })
}

export function useImportTrainingSkillItems(trainingId: string) {
  const qc = useQueryClient()
  return useMutation<TrainingSkillItemImportResult, Error, string>({
    mutationFn: (csvText: string) => api.postRaw(`${skillItemsPath(trainingId)}/import`, csvText),
    onSuccess: () => qc.invalidateQueries({ queryKey: skillItemsKey(trainingId) }),
  })
}
